import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core'

import { FrontendCard as Card, CardTemplate, Choice, ChoiceType, FrontendCardTemplate, randomizeTemplateChoices, StudyPhase, StudySetting } from '@nx-superprep/backend/firebase'
import { ResponsiveComponent } from '@nx-superprep/shared'
import { AssetType, log } from '@nx-superprep/utils'

import * as React from 'react'
import { Root, createRoot } from 'react-dom/client'

import * as GP from '@longtail-education/superprep-gameplays-react'
import { GameplayControl } from '@longtail-education/superprep-gameplays-react'

import SafeGameplayWrapper from './safe-gameplay-wrapper'
import { isEqual } from 'lodash-es'

const gameplayContainerElementRef = 'gameplaycontainer'
const reactGameplayElementRef = 'reactgameplay'
type TSXGameplayStateType = 'newCard' | 'sameCard' | 'onPresentQuestion' | 'onAnswerQuestion' | 'onPresentAnswer' | 'onFinishCard'

@Component({
  standalone: true,
  selector: 'gameplay-player-tsx',
  templateUrl: './gameplay-player-tsx.component.html',
  styleUrls: ['./gameplay-player-tsx.component.scss'],
})
export class GameplayPlayerTSXComponent extends ResponsiveComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() setting!: StudySetting
  @Input() phase!: StudyPhase
  @Input() progress = 0
  @Input() isAutoplay = false
  @Input() rightAnswerDisplayTime = 0
  @Input() wrongAnswerDisplayTime = 0

  private _didAnswer = false
  @Input()
  public get didAnswer() {
    return this._didAnswer && this._state !== 'newCard'
  }
  public set didAnswer(value) {
    this._didAnswer = value
  }

  private _template: CardTemplate | undefined
  private _card: Card | undefined
  @Input()
  public get card(): Card | undefined {
    return this._card
  }
  public set card(value: Card | undefined) {
    if (this._state === 'onFinishCard' && this.root) { this.root.unmount(); this.root = undefined }
    this._state = this._card?.id !== value?.id ? 'newCard' : 'sameCard'
    if (this._state === 'newCard' && this.root) { this.root.unmount(); this.root = undefined }
    this._card = value
    this._template = this._card ? randomizeTemplateChoices(this._card.template) : undefined
  }

  @Output() selected = new EventEmitter<Choice>()
  @Output() action = new EventEmitter<string>()

  @ViewChild(gameplayContainerElementRef, { static: true }) gameplayContainerRef: ElementRef<Element> | undefined
  @ViewChild(reactGameplayElementRef, { static: true }) reactGameplayRef: ElementRef<Element> | undefined

  root: Root | undefined = undefined

  private _didAnswerCorrectly = false
  private _choices: string[] = []
  private _state: TSXGameplayStateType = 'onFinishCard'

  getChoice(isAnswer = false) {
    return this.card?.choices.find(c => isAnswer ? c.isAnswer : !c.isAnswer)
  }
  getChoices(isAnswer = false) {
    return isAnswer ? this.card?.template.cardAnswer.answerTexts ?? [] : []
  }

  get hasAudio() {
    return !!(this.card?.question?.hasAudio || this.card?.answer?.hasAudio)
  }

  //-------------------------------------------------------------------------------------------------
  // Life cycle
  //-------------------------------------------------------------------------------------------------

  ngAfterViewInit() {
    if (!this.root) { this.render() }
  }

  ngOnChanges(changes: SimpleChanges): void {
    log.debug(changes)
    if (this.shouldRender(changes)) {
      this.render()
    }
  }

  private shouldRender(changes: SimpleChanges): boolean {
    const needsFullRender = changes['card']?.currentValue?.id !== changes['card']?.previousValue?.id
    return needsFullRender || !!changes['didAnswer'] || !!changes['phase']
  }

  override onResize(event: Event): void {
    super.onResize(event)
    this.render()
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy()
    try {
      this.root?.unmount()
    } catch (error) {
      log.error('Error unmounting React root:', error)
    } finally {
      this.root = undefined
    }
  }

  /**
   * Forces an answer (correct or incorrect) programmatically.
   * @param isCorrect - Whether the forced answer should be correct or incorrect.
   */
  onForceAnswer(isCorrect = Math.random() < 0.5): void {
    if (!this.card) return;

    // Get the correct or incorrect choice
    const choice = this.getChoice(isCorrect);
    if (!choice) return;

    // Simulate the user selecting the choice
    this._didAnswerCorrectly = isCorrect;
    this._choices = [choice.name]; // Assuming choice.name is the identifier used in React gameplays

    // Emit the selected event
    this.selected.emit(choice);

    // Trigger the React gameplay lifecycle
    this.onAnswerQuestion(this._choices);
    this.onPresentAnswer(this.card.template);
    this.onFinishCard(this.card.template);
  }

  //-------------------------------------------------------------------------------------------------
  // React gameplay
  //-------------------------------------------------------------------------------------------------

  /**
   * Calculate the width and height of the gameplay container with fallback and maximum values.
   *
   * @param gameplayContainerRef - Reference to the gameplay container element.
   * @param screenWidth - Fallback screen width.
   * @param screenHeight - Fallback screen height.
   * @returns An object containing the calculated width and height.
   */
  calculateGameplayDimensions(
    gameplayContainerRef: { nativeElement: { clientWidth: number, clientHeight: number } } | null | undefined,
    screenWidth: number,
    screenHeight: number
  ): { width: number, height: number } {
    // Define default dimensions
    const defaultWidth = 1200
    const defaultHeight = 800

    // Extract the container element
    const containerElement = gameplayContainerRef?.nativeElement

    // Calculate the width, ensuring it's between the default and a maximum of 1280
    const width = Math.min(
      containerElement?.clientWidth ?? screenWidth ?? defaultWidth,
      1280
    ) || defaultWidth

    // Calculate the height, ensuring it's between the default and a maximum of 800
    const height = Math.min(
      containerElement?.clientHeight ?? screenHeight ?? defaultHeight,
      defaultHeight
    ) || defaultHeight

    return { width, height }
  }

  render() {
    try {
      log.debug(`render ${this.card?.id ?? ''} ${this._state}`)
      if (!this.reactGameplayRef || !this.card || this._state === 'onFinishCard') { return }

      this.root ??= createRoot(this.reactGameplayRef.nativeElement)
      const { width, height } = this.calculateGameplayDimensions(this.gameplayContainerRef, this.screenWidth, this.screenHeight)
      const fileUrls = this.getFileURLs(this.card)
      const selectedChoices: string[] = this.didAnswer ? this._choices : []
      const gameplayControl = this.didAnswer
        ? this._didAnswerCorrectly ? GameplayControl.PresentAnswerWithCorrectResponse : GameplayControl.PresentAnswerWithIncorrectResponse
        : GameplayControl.PresentQuestion
      const enableAnimation = this.setting.animation

      const gameplay = createGameplay(
        this._template,
        fileUrls,
        width,
        height,
        this.rightAnswerDisplayTime,
        this.wrongAnswerDisplayTime,
        enableAnimation,
        selectedChoices,
        gameplayControl,
        (template) => this.onPresentQuestion(template),
        (choices) => this.onAnswerQuestion(choices),
        (template) => this.onPresentAnswer(template),
        (template) => this.onFinishCard(template),
      )
      this.root.render(<React.Fragment><SafeGameplayWrapper>{gameplay ?? `no React gameplay for ${this.card.id}`}</SafeGameplayWrapper></React.Fragment>)
    } catch (error) {
      log.error('Error rendering React gameplay:', error)
    }
  }

  convertAssetType(assetType: string | undefined) {
    switch (assetType) {
      case AssetType.image: return GP.AssetType.Image
      case AssetType.audio: return GP.AssetType.Audio
      case AssetType.video: return GP.AssetType.Video
      case AssetType.svg: return GP.AssetType.Diagram
      default: return undefined
    }
  }

  getFileURLs(card: Card) {
    const map = new Map<string, string>()
    const resources = card.template.resources || []
    resources.forEach(r => {
      if (r.name && r.url.length > 0) {
        const assetName = r.name
        const assetType = this.convertAssetType(r.assetType)
        if (assetType) {
          const key = GP.AssetKeyGenerator.key(assetName, assetType)
          map.set(key, r.url)
        }
      }
    })
    return map
  }

  onPresentQuestion(template: unknown) {
    log.debug('onPresentQuestion', template)
    this._state = 'onPresentQuestion'
    this._didAnswerCorrectly = false
    this._choices = []
    this.action.emit('onPresentQuestion')
  }
  onAnswerQuestion(choices: string[]) {
    log.debug('onAnswerQuestion', choices)
    this._state = 'onAnswerQuestion'
    this._choices = choices;
    const correctChoice = this.getChoices(true)
    const choice = this.card?.answer && isEqual(choices, correctChoice) ? this.card.answer : new Choice({type: ChoiceType.wrong, name:choices.join('|')})
    this._didAnswerCorrectly = choice.isAnswer
    this.selected.emit(choice)
  }
  onPresentAnswer(template: unknown) {
    log.debug('onPresentAnswer', template)
    this._state = 'onPresentAnswer'
    this.action.emit('onPresentAnswer')
  }
  onFinishCard(template: unknown) {
    log.debug('onFinishCard', template)
    this._state = 'onFinishCard'
    this.action.emit('onFinishCard')
  }
}

export function createGameplay(
  card: FrontendCardTemplate|CardTemplate|undefined,
  fileUrls = new Map<string, string>(),
  width = 0,
  height = 0,
  rightAnswerDisplayTime = 0,
  wrongAnswerDisplayTime = 0,
  enableAnimation = true,
  selectedChoices: string[] = [],
  gameplayControl = GameplayControl.PresentQuestion,
  onPresentQuestion: (template: unknown) => void = () => { return },
  onAnswerQuestion: (choices: string[]) => void = () => { return },
  onPresentAnswer: (template: unknown) => void = () => { return },
  onFinishCard: (template: unknown) => void = () => { return },
) {
  return card
  ? new GP.GameplayFactory(card).selectGameplay({
      card,
      rightAnswerDisplayTime,
      wrongAnswerDisplayTime,
      gameplayStyle: GP.GameplayStyle.PopStudy, // This prop is problematic
      onPresentQuestion,
      onAnswerQuestion,
      onPresentAnswer,
      onFinishCard,
      fileUrls,
      width,
      height,
      enableAnimation,
      gameplayControl,
      selectedChoices,
    })
  : undefined;
}
